import { useEffect, useRef } from "react";
import { useAuthStore } from "@/state/authStore";
import { useProfileStore } from "@/state/profileStore";
import { useWorkoutCompletionStore } from "@/state/workoutCompletionStore";
import { useWorkoutStore } from "@/state/workoutStore";
import { profileService } from "@/services/profileService";
import { eventService } from "@/services/eventService";
import { checkInService } from "@/services/checkInService";
import { completionService } from "@/services/completionService";
import { workoutService } from "@/services/workoutService";
import { getWeekStart } from "@/lib/workouts/mockWorkouts";
import { toast } from "@/components/Toast/useToast";
import { supabase } from "@/lib/supabase/client";

/**
 * useDataSync
 *
 * Loads all user data from Supabase into the Zustand stores on login.
 * Supabase is the single source of truth — stores hold no localStorage cache.
 *
 * Strategy:
 *   - Wait until initializeAuth() has resolved (_hasHydrated = true).
 *   - On confirmed login: fetch all data from DB and populate stores.
 *   - On confirmed logout: clear all in-memory store state.
 *   - A ref ensures the DB fetch only runs once per session.
 */
export function useDataSync() {
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const accountId = useAuthStore((s) => s.account?.id);
  const hasSyncedRef = useRef(false);

  useEffect(() => {
    if (!hasHydrated) return;

    if (!accountId) {
      // Confirmed logout — clear all in-memory state, then reset sync flag.
      // Reset AFTER clears so no concurrent re-render can trigger a new sync
      // before the stores are actually empty.
      useProfileStore.getState().clearProfile();
      useWorkoutCompletionStore.getState().clearCompletions();
      useWorkoutStore.getState().clearWorkouts();
      hasSyncedRef.current = false;
      return;
    }

    if (hasSyncedRef.current) return;
    hasSyncedRef.current = true;

    void (async () => {
      // Use allSettled so a single service failure never blocks the others.
      const [
        profileResult,
        eventResult,
        checkInsResult,
        completionsResult,
        workoutsResult,
      ] = await Promise.allSettled([
        profileService.load(accountId),
        eventService.load(accountId),
        checkInService.loadAll(accountId),
        completionService.loadAll(accountId),
        workoutService.load(accountId, getWeekStart()),
      ]);

      let anyFailed = false;

      // ── Profile ─────────────────────────────────────────────────────────────
      if (profileResult.status === "fulfilled") {
        if (profileResult.value) {
          const p = profileResult.value;
          useProfileStore.setState({
            profile: p,
            // Restore the in-memory ftpHistory from the embedded profile field
            // so the FTP sparkline and history chart are correct on every device.
            ftpHistory: p.ftpHistory ?? [],
          });
          // Hydrate Wahoo sync map/hashes from profile → merges with localStorage
          useWorkoutStore.getState().hydrateSyncFromProfile();
        }
      } else {
        console.error(
          "[useDataSync] profile load failed:",
          profileResult.reason,
        );
        anyFailed = true;
      }

      // ── Event profile ────────────────────────────────────────────────────────
      if (eventResult.status === "fulfilled") {
        if (eventResult.value) {
          useProfileStore.setState({ event: eventResult.value });
        }
      } else {
        console.error("[useDataSync] event load failed:", eventResult.reason);
        anyFailed = true;
      }

      // ── Weekly check-ins ─────────────────────────────────────────────────────
      if (checkInsResult.status === "fulfilled") {
        if (checkInsResult.value.length > 0) {
          useProfileStore.setState({ checkIns: checkInsResult.value });
        }
      } else {
        console.error(
          "[useDataSync] check-ins load failed:",
          checkInsResult.reason,
        );
        anyFailed = true;
      }

      // ── Workout completions ──────────────────────────────────────────────────
      if (completionsResult.status === "fulfilled") {
        if (completionsResult.value.length > 0) {
          useWorkoutCompletionStore.setState({
            completions: completionsResult.value,
          });
          // Rebuild power records from DB completions — addCompletion() normally
          // handles this but is bypassed by the direct setState above.
          for (const c of completionsResult.value) {
            if (c.powerNpWatts || c.bestEfforts) {
              useProfileStore.getState().updatePowerRecords(c);
            }
          }
        }
      } else {
        console.error(
          "[useDataSync] completions load failed:",
          completionsResult.reason,
        );
        anyFailed = true;
      }

      // ── Generated workouts ───────────────────────────────────────────────────
      if (workoutsResult.status === "fulfilled") {
        if (workoutsResult.value) {
          useWorkoutStore.setState({
            workouts: workoutsResult.value.workouts,
            isWeekLocked: workoutsResult.value.isWeekLocked,
            workoutsLoaded: true,
          });
        } else {
          useWorkoutStore.setState({ workoutsLoaded: true });
        }
      } else {
        console.error(
          "[useDataSync] workouts load failed:",
          workoutsResult.reason,
        );
        anyFailed = true;
      }

      if (anyFailed) {
        toast.error("Some data could not be loaded", {
          message: "Check your connection and refresh the page.",
        });
        // Reset so the next auth state change can trigger a retry.
        hasSyncedRef.current = false;
      }
    })();
  }, [hasHydrated, accountId]);

  // Realtime subscription — refresh completions the moment a Wahoo webhook
  // upserts a row, without the user needing to refresh the page.
  useEffect(() => {
    if (!accountId) return;

    const channel = supabase
      .channel(`completions-${accountId}`)
      .on(
        "postgres_changes",
        {
          event: "*", // INSERT and UPDATE
          schema: "public",
          table: "workout_completions",
          filter: `user_id=eq.${accountId}`,
        },
        () => {
          void useWorkoutCompletionStore.getState().loadFromDB(accountId);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel).catch((err: unknown) => {
        console.warn("[useDataSync] failed to remove realtime channel:", err);
      });
    };
  }, [accountId]);
}
